import { FieldValidation } from '@bybeam/platform-types';
import AddressAutocompleteInput from '@platform-ui-common/components/AddressAutocompleteInput/index.js';
import Checkbox from '@platform-ui-common/components/Checkbox/index.js';
import ManualAddressInput from '@platform-ui-common/components/ManualAddressInput/index.js';
import TextInput from '@platform-ui-common/components/TextInput/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import {
  Action,
  GenericFormData,
  ValidationErrors,
  fieldUpdate,
} from '@platform-ui-common/hooks/useForm/index.js';
import { GeocoderFilter, GeocoderResult } from '@platform-ui-common/hooks/useGeocoder.js';
import {
  formatAddressFields,
  getAddressKey,
  getUnhousedKey,
  resetAddress,
} from '@platform-ui-common/utilities/address.js';
import { formatCityLabel } from '@platform-ui-common/utilities/format.js';
import React, { useState, useEffect, useRef, useCallback } from 'react';

interface AddressFieldProps {
  answers: GenericFormData;
  dispatch: React.Dispatch<Action<GenericFormData>>;
  errors?: ValidationErrors<GenericFormData>;
  field: {
    key?: string;
    validation?: FieldValidation;
    filter?: GeocoderFilter;
    copyAddressKey?: string;
    copy?: string;
  };
  handleCopyAddress?: (value: boolean) => void;
  handleFurtherAddressChange?: (value: GeocoderResult) => void;
  handleFurtherChangeApartment?: (value: string) => void;
  handleFurtherManualFieldChange?: (fieldKey: string, value: string) => void;
  id?: string;
  showManualAddressButton?: (show: boolean) => void;
  showManualInput: boolean;
  disabled: boolean;
}

export default function AddressField({
  answers: formData,
  dispatch,
  errors,
  field: { key, validation, filter, copyAddressKey, copy },
  handleCopyAddress,
  handleFurtherAddressChange,
  handleFurtherChangeApartment,
  handleFurtherManualFieldChange,
  id,
  showManualAddressButton,
  showManualInput,
  disabled,
}: AddressFieldProps): JSX.Element {
  const showStreetAddress = !formData[getUnhousedKey(key)];

  const [geocodedAddress, setGeocodedAddress] = useState<GeocoderResult>({
    address: {
      id: `${key}Id`,
      label: formatAddressFields(
        [showStreetAddress && 'addressLine1', 'city', 'state', 'zip'],
        formData,
        key,
      ),
      stateCode: formatAddressFields(['state'], formData, key),
      state: formatAddressFields(['state'], formData, key),
      county: formatAddressFields(['county'], formData, key),
      city: formatAddressFields(['city'], formData, key),
      street: showStreetAddress && formatAddressFields(['addressLine1'], formData, key),
      postalCode: formatAddressFields(['zip'], formData, key),
      houseNumber: showStreetAddress && formatAddressFields(['addressLine2'], formData, key),
    },
  } as unknown as GeocoderResult);

  const handleAddressChange = (suggestedAddress: GeocoderResult): void => {
    if (!suggestedAddress && !validation?.required && geocodedAddress) {
      setGeocodedAddress(null);
      resetAddress(dispatch, key);
    }
    if (suggestedAddress?.address) {
      const { city, postalCode, stateCode, houseNumber, street, county } = suggestedAddress.address;
      setGeocodedAddress(suggestedAddress);
      if (city) dispatch(fieldUpdate(getAddressKey('city', key), city));
      if (postalCode) dispatch(fieldUpdate(getAddressKey('zip', key), postalCode));
      if (stateCode) dispatch(fieldUpdate(getAddressKey('state', key), stateCode));
      if (county) dispatch(fieldUpdate(getAddressKey('county', key), county));
      if (showStreetAddress)
        dispatch(
          fieldUpdate(
            getAddressKey('addressLine1', key),
            [houseNumber, street].filter(Boolean).join(' '),
          ),
        );
    }
    if (handleFurtherAddressChange) handleFurtherAddressChange(suggestedAddress);
  };

  const handleChangeApartment = (value): void => {
    if (handleFurtherChangeApartment) handleFurtherChangeApartment(value);
    dispatch(fieldUpdate(getAddressKey('addressLine2', key), value));
  };

  const handleManualFieldChange = (fieldKey: string, value: string): void => {
    if (handleFurtherManualFieldChange) handleFurtherManualFieldChange(fieldKey, value);
    dispatch(fieldUpdate(fieldKey as undefined, value));
  };
  const isCopied = !!formData[getAddressKey('copied', key)];

  const manualInputTimer = useRef<NodeJS.Timeout>();
  // Cancel the manual input timer on component unmount
  useEffect(() => () => clearTimeout(manualInputTimer.current), []);
  const initManualInputTimer = useCallback((): void => {
    if (showManualAddressButton && !isCopied) {
      manualInputTimer.current = setTimeout(() => showManualAddressButton(true), 10000);
    }
  }, [showManualAddressButton, isCopied]);

  useEffect(() => {
    clearTimeout(manualInputTimer.current);
    setGeocodedAddress({
      address: {
        id: `${key}Id`,
        label: formatAddressFields(
          [showStreetAddress && 'addressLine1', 'city', 'state', 'zip'],
          formData,
          key,
        ),
        stateCode: formatAddressFields(['state'], formData, key),
        state: formatAddressFields(['state'], formData, key),
        county: formatAddressFields(['county'], formData, key),
        city: formatAddressFields(['city'], formData, key),
        street: showStreetAddress && formatAddressFields(['addressLine1'], formData, key),
        postalCode: formatAddressFields(['zip'], formData, key),
        houseNumber: showStreetAddress && formatAddressFields(['addressLine2'], formData, key),
      },
    } as unknown as GeocoderResult);
  }, [formData, showStreetAddress, key]);

  const streetAddressLabel = copy ? copy : 'Street Address';

  return (
    <>
      {copyAddressKey && (
        <div>
          <Checkbox
            label={
              <Typography variant="body" tag="span">
                My mailing address is the same as my current address
              </Typography>
            }
            checked={isCopied}
            onClick={(value): void => handleCopyAddress(value)}
          />
        </div>
      )}
      {!showManualInput ? (
        <>
          <AddressAutocompleteInput
            id={`${id ?? key}-autocomplete`}
            label={showStreetAddress ? streetAddressLabel : 'City, State'}
            value={geocodedAddress}
            onChange={handleAddressChange}
            required={validation?.required}
            filter={filter}
            type={showStreetAddress ? 'house' : 'city'}
            key={showStreetAddress ? 'house' : 'city'}
            afterSearch={initManualInputTimer}
            disabled={disabled || isCopied}
            error={
              errors[getAddressKey(showStreetAddress ? 'addressLine1' : 'city', key)] as string
            }
            labelFormatter={!showStreetAddress && formatCityLabel}
          />
          {showStreetAddress && (
            <TextInput
              id={`${id ?? key}.addressLine2`}
              label="Apartment/Suite #"
              value={formData[getAddressKey('addressLine2', key)] ?? ''}
              onChange={(value): void => handleChangeApartment(value)}
              disabled={disabled || isCopied}
              error={errors && (errors[getAddressKey('addressLine2', key)] as string)}
            />
          )}
        </>
      ) : (
        <ManualAddressInput
          addressKey={key}
          answers={formData}
          customStreetAddressLabel={streetAddressLabel}
          handleChange={handleManualFieldChange}
          required={validation?.required}
          showStreetAddress={showStreetAddress}
          disabled={disabled || isCopied}
          errors={errors}
          showErrorMessages
        />
      )}
    </>
  );
}
